import { useMemo, useCallback } from 'react';
import DataGrid, {
  Column,
  Editing,
  Lookup,
  RowDragging,
} from 'devextreme-react/data-grid';
import { Button } from 'devextreme-react/button';
import { Slider } from 'devextreme-react/slider';
import { COLUMN_TEMPLATES, createColumnsFromTemplate } from '@calculom/report-designer';
import type { ColumnDefinition, ColumnType } from '@calculom/report-designer';

const COLUMN_TYPES: { value: ColumnType; label: string }[] = [
  { value: 'label', label: 'Label' },
  { value: 'account_code', label: 'Account Code' },
  { value: 'current', label: 'Current Period' },
  { value: 'prior_period', label: 'Prior Period' },
  { value: 'prior_year', label: 'Prior Year' },
  { value: 'budget', label: 'Budget' },
  { value: 'ytd', label: 'Year to Date' },
  { value: 'variance_amount', label: 'Variance $' },
  { value: 'variance_percent', label: 'Variance %' },
  { value: 'calculated', label: 'Calculated' },
  { value: 'period_offset', label: 'Current Period Offset' },
  { value: 'year_offset', label: 'Year Offset' },
];

const FORMAT_TYPES = [
  { value: 'currency', label: 'Currency' },
  { value: 'percent', label: 'Percent' },
  { value: 'number', label: 'Number' },
  { value: 'text', label: 'Text' },
];

interface GridRow {
  id: string;
  header: string;
  type: ColumnType;
  width?: number;
  formatType: string;
  periodOffset?: number;
  budgetName?: string;
  baseColumnId?: string;
  compareColumnId?: string;
}

export interface ColumnConfigProps {
  columns: ColumnDefinition[];
  onAddColumn: (col: ColumnDefinition) => void;
  onUpdateColumn: (id: string, updates: Record<string, unknown>) => void;
  onRemoveColumn: (id: string) => void;
  onReorderColumns: (fromIndex: number, toIndex: number) => void;
  onSetColumns: (columns: ColumnDefinition[]) => void;
}

export function ColumnConfig({
  columns,
  onAddColumn,
  onUpdateColumn,
  onRemoveColumn,
  onReorderColumns,
  onSetColumns,
}: ColumnConfigProps) {
  const gridData: GridRow[] = useMemo(
    () =>
      columns.map((col) => ({
        id: col.id,
        header: col.header,
        type: col.type,
        width: col.width,
        formatType: col.format?.type ?? 'currency',
        periodOffset: col.periodOffset,
        budgetName: col.budgetName,
        baseColumnId: col.baseColumnId,
        compareColumnId: col.compareColumnId,
      })),
    [columns],
  );

  // Lookup data for base/compare column dropdowns (non-variance, non-label columns)
  const columnLookup = useMemo(
    () =>
      columns
        .filter((c) => c.type !== 'variance_amount' && c.type !== 'variance_percent' && c.type !== 'label' && c.type !== 'account_code')
        .map((c) => ({ id: c.id, header: c.header })),
    [columns],
  );

  const hasBudgetColumn = gridData.some((r) => r.type === 'budget');
  const hasVarianceColumn = gridData.some((r) => r.type === 'variance_amount' || r.type === 'variance_percent');
  const hasOffsetColumn = gridData.some((r) => r.type === 'period_offset' || r.type === 'year_offset');

  const handleApplyTemplate = (templateId: string) => {
    const newColumns = createColumnsFromTemplate(templateId);
    if (newColumns.length > 0) {
      onSetColumns(newColumns);
    }
  };

  const handleInitNewRow = useCallback((e: { data: GridRow }) => {
    e.data.id = crypto.randomUUID();
    e.data.header = 'New Column';
    e.data.type = 'current';
    e.data.formatType = 'currency';
  }, []);

  const handleRowInserted = useCallback(
    (e: { data: GridRow }) => {
      onAddColumn({
        id: e.data.id,
        header: e.data.header,
        type: e.data.type,
        width: e.data.width,
        format: { type: e.data.formatType as 'currency' | 'percent' | 'number' | 'text' },
        periodOffset: (e.data.type === 'period_offset' || e.data.type === 'year_offset') ? (e.data.periodOffset ?? -1) : undefined,
        budgetName: e.data.type === 'budget' ? (e.data.budgetName ?? '') : undefined,
        baseColumnId: (e.data.type === 'variance_amount' || e.data.type === 'variance_percent') ? e.data.baseColumnId : undefined,
        compareColumnId: (e.data.type === 'variance_amount' || e.data.type === 'variance_percent') ? e.data.compareColumnId : undefined,
      });
    },
    [onAddColumn],
  );

  const handleRowUpdated = useCallback(
    (e: { key: string; data: Partial<GridRow> }) => {
      const updates: Record<string, unknown> = {};
      if (e.data.header !== undefined) updates['header'] = e.data.header;
      if (e.data.type !== undefined) updates['type'] = e.data.type;
      if (e.data.width !== undefined) updates['width'] = e.data.width;
      if (e.data.formatType !== undefined) {
        updates['format'] = {
          type: e.data.formatType as 'currency' | 'percent' | 'number' | 'text',
        };
      }
      if (e.data.periodOffset !== undefined) {
        updates['periodOffset'] = e.data.periodOffset;
      }
      if (e.data.type !== undefined) {
        updates['periodOffset'] = (e.data.type === 'period_offset' || e.data.type === 'year_offset') ? (e.data.periodOffset ?? -1) : undefined;
      }
      if (e.data.budgetName !== undefined) {
        updates['budgetName'] = e.data.budgetName;
      }
      if (e.data.baseColumnId !== undefined) {
        updates['baseColumnId'] = e.data.baseColumnId;
      }
      if (e.data.compareColumnId !== undefined) {
        updates['compareColumnId'] = e.data.compareColumnId;
      }
      onUpdateColumn(e.key, updates);
    },
    [onUpdateColumn],
  );

  const handleRowRemoved = useCallback(
    (e: { key: string }) => {
      onRemoveColumn(e.key);
    },
    [onRemoveColumn],
  );

  const handleReorder = useCallback(
    (e: { fromIndex: number; toIndex: number }) => {
      onReorderColumns(e.fromIndex, e.toIndex);
    },
    [onReorderColumns],
  );

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-xs text-gray-500">Quick Templates</label>
        <div className="flex flex-wrap gap-1">
          {COLUMN_TEMPLATES.map((tpl) => (
            <button
              key={tpl.id}
              onClick={() => handleApplyTemplate(tpl.id)}
              className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-100"
            >
              {tpl.name}
            </button>
          ))}
        </div>
      </div>

      <DataGrid
        dataSource={gridData}
        keyExpr="id"
        showBorders
        columnAutoWidth
        onInitNewRow={handleInitNewRow}
        onRowInserted={handleRowInserted}
        onRowUpdated={handleRowUpdated}
        onRowRemoved={handleRowRemoved}
      >
        <RowDragging
          allowReordering
          onReorder={handleReorder}
          showDragIcons
        />
        <Editing
          mode="cell"
          allowAdding
          allowUpdating
          allowDeleting
        />
        <Column dataField="header" caption="Header" />
        <Column dataField="type" caption="Type">
          <Lookup dataSource={COLUMN_TYPES} valueExpr="value" displayExpr="label" />
        </Column>
        <Column
          dataField="width"
          caption="Width"
          dataType="number"
          width={140}
          cellRender={(cellInfo: { value?: number }) => (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 w-6 text-right">
                {cellInfo.value ?? 'auto'}
              </span>
            </div>
          )}
          editCellRender={(cellInfo: { value?: number; setValue: (v: number) => void }) => (
            <div className="flex items-center gap-2 px-1">
              <Slider
                min={30}
                max={300}
                value={cellInfo.value ?? 100}
                onValueChanged={(e) => cellInfo.setValue(e.value)}
                width="100%"
                tooltip={{ enabled: true, showMode: 'always', position: 'bottom', format: (v: number) => `${v}px` }}
              />
            </div>
          )}
        />
        <Column dataField="formatType" caption="Format">
          <Lookup dataSource={FORMAT_TYPES} valueExpr="value" displayExpr="label" />
        </Column>
        <Column
          dataField="periodOffset"
          caption="Offset"
          dataType="number"
          width={80}
          visible={hasOffsetColumn}
          cellRender={(cellInfo: { data: GridRow; value?: number }) =>
            cellInfo.data.type === 'period_offset' || cellInfo.data.type === 'year_offset'
              ? <span className="text-xs">{cellInfo.value ?? 0}</span>
              : <span className="text-xs text-gray-300">&mdash;</span>
          }
        />
        <Column
          dataField="budgetName"
          caption="Budget Name"
          width={120}
          visible={hasBudgetColumn}
          cellRender={(cellInfo: { data: GridRow; value?: string }) =>
            cellInfo.data.type === 'budget'
              ? <span className="text-xs">{cellInfo.value || '(not set)'}</span>
              : <span className="text-xs text-gray-300">&mdash;</span>
          }
        />
        <Column
          dataField="baseColumnId"
          caption="Base Column"
          width={120}
          visible={hasVarianceColumn}
          cellRender={(cellInfo: { data: GridRow; value?: string }) => {
            if (cellInfo.data.type !== 'variance_amount' && cellInfo.data.type !== 'variance_percent') {
              return <span className="text-xs text-gray-300">&mdash;</span>;
            }
            const col = columnLookup.find((c) => c.id === cellInfo.value);
            return <span className="text-xs">{col?.header ?? '(auto)'}</span>;
          }}
        >
          <Lookup dataSource={columnLookup} valueExpr="id" displayExpr="header" allowClearing />
        </Column>
        <Column
          dataField="compareColumnId"
          caption="Compare Column"
          width={120}
          visible={hasVarianceColumn}
          cellRender={(cellInfo: { data: GridRow; value?: string }) => {
            if (cellInfo.data.type !== 'variance_amount' && cellInfo.data.type !== 'variance_percent') {
              return <span className="text-xs text-gray-300">&mdash;</span>;
            }
            const col = columnLookup.find((c) => c.id === cellInfo.value);
            return <span className="text-xs">{col?.header ?? '(auto)'}</span>;
          }}
        >
          <Lookup dataSource={columnLookup} valueExpr="id" displayExpr="header" allowClearing />
        </Column>
      </DataGrid>

      <Button
        text="Add Column"
        icon="plus"
        stylingMode="outlined"
        width="100%"
        onClick={() => {
          onAddColumn({
            id: crypto.randomUUID(),
            header: 'New Column',
            type: 'current',
            format: { type: 'currency' },
          });
        }}
      />
    </div>
  );
}
